/**
 * Pure aggregation logic for the GroupDetail «Needs attention» panel.
 *
 * `buildGroupAlerts` is a side-effect-free projection over the six already
 * computed source counts (debtors, below-threshold attendance, students
 * without marks, overdue contributions, failed Zoom recordings, and
 * missing/lost PandaDoc documents). It decides which alerts are shown, what
 * count each carries, and where each one deep-links to.
 *
 * This module intentionally avoids importing React, Apollo, generated GraphQL
 * types, or the GroupDetail component files: the `TabId`/`RosterFilter` unions
 * are declared here as the canonical source so callers/components import them
 * from this util rather than the other way round.
 */

/**
 * The seven GroupDetail tabs. Mirrors the inline union in
 * `components/pages/GroupDetail/GroupDetail.tsx`; declared here so the pure
 * util stays decoupled from the component tree.
 */
export type TabId =
  | 'overview'
  | 'students'
  | 'schedule'
  | 'zoom'
  | 'pandadoc'
  | 'materials'
  | 'communication'
  | 'tasks'
  | 'finances';

/**
 * The roster filter chips on the Students tab. Mirrors the union in
 * `components/pages/GroupDetail/parts/StudentRosterFilters.tsx`.
 */
export type RosterFilter =
  | 'all'
  | 'active'
  | 'terminated'
  | 'debtors'
  | 'blocked';

/** The six fixed alert categories surfaced by the panel. */
export type GroupAlertId =
  | 'debtors'
  | 'attendanceBelow'
  | 'noMarks'
  | 'overdue'
  | 'zoomFailed'
  | 'pandadocMissing';

/** Where selecting an alert navigates to (and the filter it applies, if any). */
export interface IGroupAlertTarget {
  tab: TabId;
  filter?: RosterFilter;
}

/** A single displayable alert: its category, affected-item count, and target. */
export interface IGroupAlert {
  id: GroupAlertId;
  count: number;
  target: IGroupAlertTarget;
}

/**
 * The already-computed state of one alert category as supplied by its source
 * hook: the affected-item count plus whether that source is still loading.
 */
export interface IGroupAlertSource {
  count: number;
  loading: boolean;
}

/**
 * Decoupled input for `buildGroupAlerts` — one `{ count, loading }` entry per
 * category. Callers (the `AlertsPanel`) map their six source hooks onto these
 * fields; the util never touches the hooks directly.
 */
export interface IBuildGroupAlertsInput {
  debtors: IGroupAlertSource;
  attendanceBelow: IGroupAlertSource;
  noMarks: IGroupAlertSource;
  overdue: IGroupAlertSource;
  zoomFailed: IGroupAlertSource;
  pandadocMissing: IGroupAlertSource;
}

/** The derived alerts state consumed by the panel. */
export interface IGroupAlertsState {
  /** Alerts already filtered to `count > 0`, in canonical category order. */
  alerts: IGroupAlert[];
  /** `true` iff every evaluated category has finished loading. */
  allLoaded: boolean;
  /** `true` iff at least one category is still loading. */
  anyLoading: boolean;
}

/**
 * Fixed category → navigation-target mapping. The targets are constant: an
 * alert always deep-links to the same tab (and filter) regardless of counts.
 */
export const GROUP_ALERT_TARGETS: Readonly<
  Record<GroupAlertId, IGroupAlertTarget>
> = {
  debtors: { tab: 'students', filter: 'debtors' },
  attendanceBelow: { tab: 'schedule' },
  noMarks: { tab: 'schedule' },
  overdue: { tab: 'finances' },
  zoomFailed: { tab: 'zoom' },
  pandadocMissing: { tab: 'pandadoc' },
};

/** Canonical display order for the alert categories. */
export const GROUP_ALERT_ORDER: readonly GroupAlertId[] = [
  'debtors',
  'attendanceBelow',
  'noMarks',
  'overdue',
  'zoomFailed',
  'pandadocMissing',
];

/**
 * Build the «Needs attention» alerts state from the six source counts.
 *
 * - `alerts` contains one entry per category whose `count > 0`, in canonical
 *   order, each carrying its true count and its fixed navigation target.
 * - `allLoaded` is `true` iff every category has finished loading.
 * - `anyLoading` is `true` iff at least one category is still loading.
 */
export const buildGroupAlerts = (
  input: IBuildGroupAlertsInput,
): IGroupAlertsState => {
  const alerts: IGroupAlert[] = [];
  let allLoaded = true;
  let anyLoading = false;

  GROUP_ALERT_ORDER.forEach((id) => {
    const { count, loading } = input[id];

    if (loading) {
      anyLoading = true;
      allLoaded = false;
    }

    if (count > 0) {
      alerts.push({ id, count, target: GROUP_ALERT_TARGETS[id] });
    }
  });

  return { alerts, allLoaded, anyLoading };
};

/**
 * Whether the localized «no issues» state may be shown: only once every
 * category has finished loading and none produced a positive count. Derivable
 * purely from the returned state, exposed here for callers' convenience.
 */
export const canShowNoIssues = (state: IGroupAlertsState): boolean =>
  state.allLoaded && state.alerts.length === 0;
